import { lazy, Suspense, useLayoutEffect } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation, useNavigationType } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { MobileDock, Navbar } from "@/components/navigation/Navbar";
import { AuthCallback } from "@/pages/Auth/Login";
import Home from "@/pages/Home/Home";
import Search from "@/pages/Search/Search";
import MovieDetail from "@/pages/Movie/MovieDetail";
import Category from "@/pages/Category/Category";
import Browse from "@/pages/Browse/Browse";
import { AdminGuard } from "@/components/common/AdminGuard";
import { SettingsProvider } from "@/context/SettingsContext";
import { AdsManager } from "@/components/common/Ads";
import { MaintenanceGate } from "@/components/common/MaintenanceGate";
import { SeasonalLayer } from "@/components/common/Seasonal";

// Code-split secondary routes.
const Watch = lazy(() => import("@/pages/Watch/Watch"));
const Watchlist = lazy(() => import("@/pages/Watchlist/Watchlist"));
const Profile = lazy(() => import("@/pages/Profile/Profile"));
const Login = lazy(() => import("@/pages/Auth/Login"));
const Categories = lazy(() => import("@/pages/Category/Categories"));
const AdminList = lazy(() => import("@/pages/Admin/AdminList"));
const AdminEditor = lazy(() => import("@/pages/Admin/AdminEditor"));
const AdminStats = lazy(() => import("@/pages/Admin/AdminStats"));
const AdminSettings = lazy(() => import("@/pages/Admin/AdminSettings"));
const AdminSync = lazy(() => import("@/pages/Admin/AdminSync"));
const NotFound = lazy(() => import("@/pages/NotFound"));

function ScrollManager() {
  const { pathname } = useLocation();
  const type = useNavigationType();
  useLayoutEffect(() => {
    if (type !== "POP") window.scrollTo(0, 0);
  }, [pathname, type]);
  return null;
}

const Fallback = () => <div className="min-h-[60vh]" aria-busy="true" />;

function AppRouter() {
  const location = useLocation();
  // Emergent Google Auth returns with #session_id=... — handle synchronously before routes.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-full focus:bg-white focus:px-4 focus:py-2 focus:text-black">
        Asosiy kontentga o‘tish
      </a>
      <ScrollManager />
      <AdsManager />
      <SeasonalLayer />
      <Navbar />
      <MaintenanceGate>
      <Suspense fallback={<Fallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/search" element={<Search />} />
          <Route path="/movie/:id" element={<MovieDetail />} />
          <Route path="/watch/:id" element={<Watch />} />
          <Route path="/movies" element={<Browse type="movie" key="movies" />} />
          <Route path="/series" element={<Browse type="series" key="series" />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/category/:slug" element={<Category />} />
          <Route path="/watchlist" element={<Watchlist />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin" element={<AdminGuard><AdminList /></AdminGuard>} />
          <Route path="/admin/new" element={<AdminGuard><AdminEditor key="new" /></AdminGuard>} />
          <Route path="/admin/edit/:id" element={<AdminGuard><AdminEditor /></AdminGuard>} />
          <Route path="/admin/stats" element={<AdminGuard><AdminStats /></AdminGuard>} />
          <Route path="/admin/settings" element={<AdminGuard><AdminSettings /></AdminGuard>} />
          <Route path="/admin/sync" element={<AdminGuard><AdminSync /></AdminGuard>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </MaintenanceGate>
      <MobileDock />
    </>
  );
}

export default function App() {
  return (
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <SettingsProvider>
        <AuthProvider>
          <div className="App relative z-[2]">
            <AppRouter />
          </div>
          <Toaster
            theme="dark"
            position="top-center"
            offset={20}
            toastOptions={{
              classNames: {
                toast: "!rounded-[18px] !border !border-white/10 !bg-[rgba(28,28,32,.82)] !backdrop-blur-2xl !text-white !shadow-[0_20px_60px_rgba(0,0,0,.5)] !font-semibold",
                description: "!text-white/60",
              },
            }}
          />
        </AuthProvider>
        </SettingsProvider>
      </BrowserRouter>
    </MotionConfig>
  );
}
